/*
 * SK Story — one sticky WebGL stage for the whole scroll story.
 * A 3D "inflated" hoodie (extruded silhouette with deep rounded bevel, fabric
 * sheen, knit normal map) whose back print is painted procedurally in high
 * definition. Each chapter defines a pose (rotation + point of focus + camera
 * distance); scrolling interpolates between poses while chapter texts and the
 * background gradient follow.
 */
(function () {
  if (!window.gsap || !window.ScrollTrigger) return;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isSmall = window.matchMedia('(max-width: 749px)').matches;
  var TAU = Math.PI * 2;

  var COLORS = {
    noir: { base: '#1c1b1b', ink: '#f2ede4' },
    blanc: { base: '#f3f0ea', ink: '#141211' },
    sable: { base: '#d8c19f', ink: '#141211' },
    marine: { base: '#1f2b4c', ink: '#f2ede4' },
    'vert sapin': { base: '#1d4a3e', ink: '#f2ede4' },
    rose: { base: '#efa9c2', ink: '#141211' },
  };
  var ORDER = ['noir', 'blanc', 'sable', 'marine', 'vert sapin', 'rose'];

  // Silhouette in "SVG space" (600 x 700, y down). Arms slightly open.
  var SIL = [
    ['M', 236, 170], ['C', 204, 176, 172, 184, 152, 198], ['C', 122, 250, 96, 350, 74, 470], ['L', 64, 540], ['L', 110, 552], ['L', 120, 484],
    ['C', 132, 410, 150, 340, 176, 290], ['C', 178, 390, 176, 480, 180, 560], ['L', 180, 604], ['C', 262, 616, 338, 616, 420, 604], ['L', 420, 560],
    ['C', 424, 480, 422, 390, 424, 290], ['C', 450, 340, 468, 410, 480, 484], ['L', 490, 552], ['L', 536, 540], ['L', 526, 470],
    ['C', 504, 350, 478, 250, 448, 198], ['C', 428, 184, 396, 176, 364, 170], ['C', 378, 108, 348, 64, 300, 62], ['C', 252, 64, 222, 108, 236, 170],
  ];
  var OPENING = [[256, 176], [258, 132], [280, 106], [300, 104], [320, 106], [342, 132], [344, 176], [326, 196], [300, 200], [274, 196], [256, 176]];

  function rng(seed) {
    var a = seed >>> 0;
    return function () {
      a = (a + 0x6d2b79f5) >>> 0;
      var t = a;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
  function smooth(t) { return t * t * (3 - 2 * t); }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function hexToRgb(h) { var n = parseInt(h.slice(1), 16); return [n >> 16, (n >> 8) & 255, n & 255]; }
  function mixHex(a, b, t) {
    var x = hexToRgb(a), y = hexToRgb(b);
    return 'rgb(' + Math.round(lerp(x[0], y[0], t)) + ',' + Math.round(lerp(x[1], y[1], t)) + ',' + Math.round(lerp(x[2], y[2], t)) + ')';
  }
  function shade(hex, k) {
    var c = hexToRgb(hex).map(function (v) { return Math.max(0, Math.min(255, Math.round(k > 0 ? v + (255 - v) * k : v * (1 + k)))); });
    return 'rgb(' + c[0] + ',' + c[1] + ',' + c[2] + ')';
  }

  /* ---------------- Canvas painting ---------------- */
  function silPath(ctx) {
    ctx.beginPath();
    SIL.forEach(function (s) {
      if (s[0] === 'M') ctx.moveTo(s[1], s[2]);
      else if (s[0] === 'L') ctx.lineTo(s[1], s[2]);
      else ctx.bezierCurveTo(s[1], s[2], s[3], s[4], s[5], s[6]);
    });
    ctx.closePath();
  }

  function paintFabric(ctx, base, R) {
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, 600, 700);
    // Soft volume: lighter chest, darker sides/armpits
    var g = ctx.createRadialGradient(300, 330, 40, 300, 360, 330);
    g.addColorStop(0, 'rgba(255,255,255,0.10)');
    g.addColorStop(1, 'rgba(0,0,0,0.22)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 600, 700);
    // Fabric folds: very soft, wide shading only
    ctx.lineCap = 'round';
    for (var i = 0; i < 0; i++) {
      var x = 150 + R() * 300, y = 240 + R() * 300;
      ctx.strokeStyle = 'rgba(0,0,0,' + (0.04 + R() * 0.05).toFixed(3) + ')';
      ctx.lineWidth = 6 + R() * 14;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.quadraticCurveTo(x + (R() - 0.5) * 40, y + 40 + R() * 40, x + (R() - 0.5) * 30, y + 90 + R() * 60);
      ctx.stroke();
    }
    // Ribbed cuffs and hem
    function rib(x0, y0, x1, y1, w, n) {
      ctx.save();
      ctx.strokeStyle = 'rgba(0,0,0,0.16)';
      ctx.lineWidth = 1.2;
      for (var k = 0; k <= n; k++) {
        var t = k / n;
        var ax = lerp(x0, x1, t), ay = lerp(y0, y1, t);
        ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(ax + w[0], ay + w[1]); ctx.stroke();
      }
      ctx.restore();
    }
    ctx.fillStyle = 'rgba(0,0,0,0.10)';
    ctx.fillRect(170, 566, 260, 50);
    rib(182, 566, 418, 566, [0, 44], 58);
    ctx.strokeStyle = 'rgba(0,0,0,0.25)'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(180, 566); ctx.bezierCurveTo(262, 574, 338, 574, 420, 566); ctx.stroke();
    // cuffs
    [[64, 512, 116, 522], [484, 522, 536, 512]].forEach(function (c) {
      ctx.fillStyle = 'rgba(0,0,0,0.10)';
      ctx.beginPath(); ctx.moveTo(c[0], c[1]); ctx.lineTo(c[2], c[3]); ctx.lineTo(c[2] - (c[0] < 300 ? 6 : -6), 556); ctx.lineTo(c[0] - (c[0] < 300 ? 6 : -6), 548); ctx.closePath(); ctx.fill();
      rib(c[0], c[1], c[2], c[3], [-3, 34], 14);
      ctx.strokeStyle = 'rgba(0,0,0,0.25)'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(c[0], c[1]); ctx.lineTo(c[2], c[3]); ctx.stroke();
    });
    // Shoulder seams
    ctx.strokeStyle = 'rgba(0,0,0,0.18)'; ctx.lineWidth = 1.6; ctx.setLineDash([5, 5]);
    ctx.beginPath(); ctx.moveTo(176, 290); ctx.bezierCurveTo(176, 240, 200, 200, 238, 176); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(424, 290); ctx.bezierCurveTo(424, 240, 400, 200, 362, 176); ctx.stroke();
    ctx.setLineDash([]);
  }

  function paintFront(canvas, colorKey) {
    var ctx = canvas.getContext('2d');
    var c = COLORS[colorKey];
    ctx.setTransform(canvas.width / 600, 0, 0, canvas.height / 700, 0, 0);
    paintFabric(ctx, c.base, rng(3));
    // Hood: outer shading and face opening
    ctx.fillStyle = 'rgba(0,0,0,0.12)';
    ctx.beginPath(); ctx.moveTo(236, 170); ctx.bezierCurveTo(222, 108, 252, 64, 300, 62); ctx.bezierCurveTo(348, 64, 378, 108, 364, 170); ctx.bezierCurveTo(340, 192, 260, 192, 236, 170); ctx.fill();
    var inner = ctx.createRadialGradient(300, 150, 6, 300, 160, 60);
    inner.addColorStop(0, 'rgba(0,0,0,0.85)');
    inner.addColorStop(1, 'rgba(0,0,0,0.55)');
    ctx.fillStyle = inner;
    ctx.beginPath();
    OPENING.forEach(function (p, i) { if (i) ctx.lineTo(p[0], p[1]); else ctx.moveTo(p[0], p[1]); });
    ctx.closePath(); ctx.fill();
    // neck rib
    ctx.strokeStyle = 'rgba(0,0,0,0.22)'; ctx.lineWidth = 2.4;
    ctx.beginPath(); ctx.moveTo(240, 182); ctx.bezierCurveTo(262, 214, 338, 214, 360, 182); ctx.stroke();
  }

  function flower(ctx, x, y, r, rot, ink, R) {
    for (var i = 0; i < 5; i++) {
      var a = rot + (i * TAU) / 5;
      var px = x + Math.cos(a) * r * 0.55, py = y + Math.sin(a) * r * 0.55;
      var g = ctx.createRadialGradient(x, y, r * 0.1, px, py, r * 0.75);
      g.addColorStop(0, '#d9708a');
      g.addColorStop(0.45, '#f6c6d2');
      g.addColorStop(1, '#fff1f4');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.ellipse(px, py, r * 0.52, r * 0.4, a, 0, TAU);
      ctx.fill();
      ctx.strokeStyle = 'rgba(150,60,80,0.35)'; ctx.lineWidth = 0.7; ctx.stroke();
    }
    ctx.fillStyle = '#9e3b52';
    ctx.beginPath(); ctx.arc(x, y, r * 0.17, 0, TAU); ctx.fill();
    ctx.strokeStyle = '#7a2a3c'; ctx.lineWidth = 0.6;
    for (var s = 0; s < 8; s++) {
      var b = (s / 8) * TAU + R();
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + Math.cos(b) * r * 0.36, y + Math.sin(b) * r * 0.36); ctx.stroke();
      ctx.fillStyle = '#f2c94c';
      ctx.beginPath(); ctx.arc(x + Math.cos(b) * r * 0.38, y + Math.sin(b) * r * 0.38, 0.9, 0, TAU); ctx.fill();
    }
  }

  function branch(ctx, x, y, angle, len, width, depth, ink, R, flowers) {
    if (depth > 5 || len < 6) { flowers.push([x, y]); return; }
    var ex = x + Math.cos(angle) * len, ey = y + Math.sin(angle) * len;
    var cx = (x + ex) / 2 + (R() - 0.5) * len * 0.5, cy = (y + ey) / 2 + (R() - 0.5) * len * 0.5;
    ctx.strokeStyle = ink; ctx.lineWidth = width; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(x, y); ctx.quadraticCurveTo(cx, cy, ex, ey); ctx.stroke();
    // bark texture
    ctx.lineWidth = Math.max(0.5, width * 0.18);
    ctx.strokeStyle = 'rgba(128,128,128,0.35)';
    ctx.beginPath(); ctx.moveTo(x + 1, y); ctx.quadraticCurveTo(cx + 1, cy, ex + 1, ey); ctx.stroke();
    if (depth >= 2 && R() < 0.7) flowers.push([lerp(x, ex, 0.6) + (R() - 0.5) * 16, lerp(y, ey, 0.6) + (R() - 0.5) * 16]);
    var kids = depth < 2 ? 2 : R() < 0.35 ? 3 : 2;
    for (var k = 0; k < kids; k++) {
      branch(ctx, ex, ey, angle + (R() - 0.5) * 1.3 + (k ? 0.35 : -0.35), len * (0.62 + R() * 0.2), width * 0.66, depth + 1, ink, R, flowers);
    }
  }

  function paintBack(canvas, colorKey) {
    var ctx = canvas.getContext('2d');
    var c = COLORS[colorKey];
    var ink = c.ink;
    var dark = c.ink !== '#141211';
    ctx.setTransform(canvas.width / 600, 0, 0, canvas.height / 700, 0, 0);
    paintFabric(ctx, c.base, rng(5));
    // Mirror so the print reads correctly when seen from behind.
    ctx.save();
    ctx.translate(600, 0);
    ctx.scale(-1, 1);
    // Hood seen from behind
    ctx.fillStyle = 'rgba(0,0,0,0.10)';
    ctx.beginPath(); ctx.moveTo(234, 172); ctx.bezierCurveTo(220, 104, 254, 60, 300, 58); ctx.bezierCurveTo(346, 60, 380, 104, 366, 172); ctx.bezierCurveTo(344, 194, 256, 194, 234, 172); ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.22)'; ctx.lineWidth = 1.6; ctx.setLineDash([5, 5]);
    ctx.beginPath(); ctx.moveTo(300, 60); ctx.bezierCurveTo(302, 110, 300, 150, 300, 186); ctx.stroke();
    ctx.setLineDash([]);

    var R = rng(21);
    var flowers = [];
    // Cherry branches behind the blade
    branch(ctx, 214, 520, -1.15, 70, 9, 0, ink, R, flowers);
    branch(ctx, 300, 470, -0.55, 56, 7, 1, ink, R, flowers);
    branch(ctx, 300, 420, -2.45, 50, 6, 1, ink, R, flowers);
    branch(ctx, 300, 360, -0.8, 40, 5, 2, ink, R, flowers);

    // Katana: saya (scabbard), tsuba (guard), tsuka (handle) with ito wrap
    var gold = '#c9a24a';
    var lac = dark ? '#3a3534' : '#151312';
    ctx.fillStyle = lac;
    ctx.beginPath(); ctx.moveTo(293, 304); ctx.lineTo(307, 304); ctx.lineTo(306, 528); ctx.quadraticCurveTo(300, 542, 294, 528); ctx.closePath(); ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.35)'; ctx.lineWidth = 1.2;
    ctx.beginPath(); ctx.moveTo(296, 310); ctx.lineTo(296, 520); ctx.stroke();
    ctx.fillStyle = gold; ctx.fillRect(292, 516, 16, 6); ctx.fillRect(292.5, 380, 15, 4);
    ctx.strokeStyle = '#b3262e'; ctx.lineWidth = 3; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(307, 318); ctx.bezierCurveTo(334, 330, 318, 356, 338, 372); ctx.bezierCurveTo(350, 382, 344, 398, 332, 402); ctx.stroke();
    // tsuba
    var tg = ctx.createRadialGradient(300, 296, 2, 300, 296, 24);
    tg.addColorStop(0, '#e6c56a'); tg.addColorStop(1, '#8a6a22');
    ctx.fillStyle = tg;
    ctx.beginPath(); ctx.ellipse(300, 296, 24, 8, 0, 0, TAU); ctx.fill();
    ctx.strokeStyle = '#5c4512'; ctx.lineWidth = 1; ctx.stroke();
    ctx.fillStyle = gold; ctx.fillRect(294, 287, 12, 8);
    // tsuka
    ctx.fillStyle = '#f1e8d6'; ctx.fillRect(292, 196, 16, 90);
    ctx.fillStyle = dark ? '#2b2626' : '#141211';
    for (var y = 198; y < 284; y += 11) {
      ctx.beginPath(); ctx.moveTo(292, y); ctx.lineTo(308, y + 5.5); ctx.lineTo(292, y + 11); ctx.closePath(); ctx.fill();
      ctx.beginPath(); ctx.moveTo(308, y); ctx.lineTo(292, y + 5.5); ctx.lineTo(308, y + 11); ctx.closePath(); ctx.fill();
    }
    ctx.fillStyle = gold; ctx.fillRect(291, 188, 18, 9);

    // Blossoms (over branches, some over the blade)
    var FR = rng(8);
    flowers.forEach(function (f, i) {
      var r = 9 + FR() * 9;
      if (FR() < 0.2) {
        ctx.fillStyle = '#e48aa1';
        ctx.beginPath(); ctx.ellipse(f[0], f[1], r * 0.28, r * 0.42, FR() * 3, 0, TAU); ctx.fill();
      } else flower(ctx, f[0], f[1], r, FR() * TAU, ink, FR);
    });
    // drifting petals
    for (var p = 0; p < 16; p++) {
      ctx.fillStyle = 'rgba(241,170,190,0.9)';
      ctx.beginPath(); ctx.ellipse(220 + FR() * 180, 220 + FR() * 330, 3.5, 2.2, FR() * 3, 0, TAU); ctx.fill();
    }

    // Calligraphy column and seal
    ctx.fillStyle = ink;
    ctx.font = '500 21px "Noto Serif JP", serif';
    ctx.textAlign = 'center';
    var text = '花は桜木人は武士';
    for (var k = 0; k < text.length; k++) ctx.fillText(text[k], 338, 330 + k * 24 + (k > 3 ? 10 : 0));
    ctx.fillStyle = '#b3262e';
    ctx.fillRect(326, 538, 24, 24);
    ctx.fillStyle = '#f3e6e1';
    ctx.font = '500 17px "Noto Serif JP", serif';
    ctx.fillText('武', 338, 556);
    ctx.restore();
  }

  function knitNormal(THREE, size) {
    var c = document.createElement('canvas');
    c.width = c.height = size;
    var g = c.getContext('2d');
    var img = g.createImageData(size, size);
    for (var y = 0; y < size; y++) {
      for (var x = 0; x < size; x++) {
        var i = (y * size + x) * 4;
        var col = Math.sin((x / size) * TAU * 8);
        var row = Math.sin((y / size) * TAU * 16 + (x % (size / 8) < size / 16 ? 0 : Math.PI));
        img.data[i] = 128 + col * 34;
        img.data[i + 1] = 128 + row * 22;
        img.data[i + 2] = 255;
        img.data[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
    var t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return t;
  }

  /* ---------------- Geometry ---------------- */
  function buildHoodie(THREE, frontTex, backTex, knit) {
    var S = new THREE.Shape();
    SIL.forEach(function (s) {
      function X(v) { return v - 300; }
      function Y(v) { return 350 - v; }
      if (s[0] === 'M') S.moveTo(X(s[1]), Y(s[2]));
      else if (s[0] === 'L') S.lineTo(X(s[1]), Y(s[2]));
      else S.bezierCurveTo(X(s[1]), Y(s[2]), X(s[3]), Y(s[4]), X(s[5]), Y(s[6]));
    });
    var geo = new THREE.ExtrudeGeometry(S, {
      depth: 70,
      bevelEnabled: true,
      bevelThickness: 46,
      bevelSize: 22,
      bevelSegments: isSmall ? 8 : 14,
      curveSegments: isSmall ? 28 : 48,
    });
    geo.translate(0, 0, -35);

    // Split the cap group into front / back so each gets its own texture.
    var pos = geo.attributes.position, nor = geo.attributes.normal, uv = geo.attributes.uv;
    var buckets = [[], [], []];
    geo.groups.forEach(function (gr) {
      for (var t = gr.start; t < gr.start + gr.count; t += 3) {
        var target = gr.materialIndex === 1 ? 2 : nor.getZ(t) > 0 ? 0 : 1;
        buckets[target].push(t);
      }
    });
    var total = buckets[0].length + buckets[1].length + buckets[2].length;
    var P = new Float32Array(total * 9), Nn = new Float32Array(total * 9), U = new Float32Array(total * 6);
    var w = 0;
    var g2 = new THREE.BufferGeometry();
    var start = 0;
    buckets.forEach(function (list, mi) {
      list.forEach(function (t) {
        for (var v = 0; v < 3; v++) {
          var src = t + v;
          P[w * 3] = pos.getX(src); P[w * 3 + 1] = pos.getY(src); P[w * 3 + 2] = pos.getZ(src);
          Nn[w * 3] = nor.getX(src); Nn[w * 3 + 1] = nor.getY(src); Nn[w * 3 + 2] = nor.getZ(src);
          // Caps: map shape coords to 0..1. Sides: keep generated uv (scaled) for the knit.
          if (mi < 2) { U[w * 2] = (P[w * 3] + 300) / 600; U[w * 2 + 1] = (P[w * 3 + 1] + 350) / 700; }
          else { U[w * 2] = uv.getX(src) / 120; U[w * 2 + 1] = uv.getY(src) / 120; }
          w++;
        }
      });
      g2.addGroup(start, list.length * 3, mi);
      start += list.length * 3;
    });
    // Smooth the rounded bevel: average face normals of the side triangles per position.
    var sideStart = (buckets[0].length + buckets[1].length) * 3;
    var acc = {};
    function keyOf(i) { return Math.round(P[i * 3] * 10) + '_' + Math.round(P[i * 3 + 1] * 10) + '_' + Math.round(P[i * 3 + 2] * 10); }
    for (var vi = sideStart; vi < w; vi++) {
      var kq = keyOf(vi);
      if (!acc[kq]) acc[kq] = new THREE.Vector3();
      acc[kq].x += Nn[vi * 3]; acc[kq].y += Nn[vi * 3 + 1]; acc[kq].z += Nn[vi * 3 + 2];
    }
    for (var vj = sideStart; vj < w; vj++) {
      var nv = acc[keyOf(vj)];
      if (!nv) continue;
      var nn = nv.clone().normalize();
      Nn[vj * 3] = nn.x; Nn[vj * 3 + 1] = nn.y; Nn[vj * 3 + 2] = nn.z;
    }
    g2.setAttribute('position', new THREE.BufferAttribute(P, 3));
    g2.setAttribute('normal', new THREE.BufferAttribute(Nn, 3));
    g2.setAttribute('uv', new THREE.BufferAttribute(U, 2));
    geo.dispose();

    function fabric(map) {
      var m = new THREE.MeshPhysicalMaterial({
        color: 0xffffff,
        map: map || null,
        roughness: 0.92,
        sheen: 1,
        sheenRoughness: 0.75,
        sheenColor: new THREE.Color(0xffffff),
        normalMap: knit,
        normalScale: new THREE.Vector2(0.35, 0.35),
      });
      return m;
    }
    var sideMat = fabric(null);
    var mesh = new THREE.Mesh(g2, [fabric(frontTex), fabric(backTex), sideMat]);

    // Hood rim: a soft tube around the face opening for relief.
    var pts = OPENING.map(function (p) { return new THREE.Vector3(p[0] - 300, 350 - p[1], 81); });
    var curve = new THREE.CatmullRomCurve3(pts, true, 'centripetal');
    var rim = new THREE.Mesh(new THREE.TubeGeometry(curve, 80, 9, 12, true), sideMat);

    var group = new THREE.Group();
    group.add(mesh);
    group.add(rim);
    group.scale.setScalar(1 / 200);
    return { group: group, sideMat: sideMat, mats: mesh.material.concat([]), frontZ: 81, backZ: -81 };
  }

  /* ---------------- Story ---------------- */
  function setup(section) {
    if (section.hasAttribute('data-sk-story-init')) return;
    section.setAttribute('data-sk-story-init', '');
    var stage = section.querySelector('[data-story-stage]');
    var canvas = section.querySelector('[data-story-canvas]');
    var chapters = Array.from(section.querySelectorAll('[data-chapter]'));
    var nameEl = section.querySelector('[data-story-color]');
    var N = chapters.length;
    var poses = chapters.map(function (c) {
      return {
        ry: parseFloat(c.getAttribute('data-ry')) || 0,
        rx: parseFloat(c.getAttribute('data-rx')) || 0,
        face: c.getAttribute('data-face') || 'front',
        fx: parseFloat(c.getAttribute('data-fx')) || 300,
        fy: parseFloat(c.getAttribute('data-fy')) || 340,
        dist: parseFloat(c.getAttribute('data-dist')) || 7.5,
        bg: c.getAttribute('data-bg') || '#141110',
        color: (c.getAttribute('data-color') || '').toLowerCase(),
        cycle: c.hasAttribute('data-cycle'),
        sy: parseFloat(c.getAttribute('data-sy')) || 0,
        intro: c.hasAttribute('data-intro'),
      };
    });
    var progress = 0;

    // If the story starts with an "intro" pose and follows the hero, slide it up over
    // the hero: the hoodie peeks at the bottom of the first screen, then takes over.
    var wrapper = section.closest('.shopify-section') || section;
    var prev = wrapper.previousElementSibling;
    if (poses[0].intro && prev && prev.querySelector('[data-sk-hero]')) {
      // Hidden until the first frame computes it, so the dark stage never covers the hero.
      var introBg = section.querySelector('[data-story-bg]');
      if (introBg) introBg.style.opacity = '0';
      var overlap = function () {
        wrapper.style.position = 'relative';
        wrapper.style.zIndex = '2';
        wrapper.style.marginTop = -prev.offsetHeight + 'px';
      };
      overlap();
      var lastW = window.innerWidth;
      window.addEventListener('resize', function () {
        if (window.innerWidth === lastW) return;
        lastW = window.innerWidth;
        overlap();
        ScrollTrigger.refresh();
      });
    }

    // Event-driven progress: ScrollTrigger reads the scroll once per scroll event for every trigger, and
    // onRefresh resyncs it after load / resize / restored scroll. Reading scrollY or a rect in frame()
    // instead forced a style + layout pass on every frame.
    function syncProgress(self) { progress = self.progress; }
    ScrollTrigger.create({ trigger: section, start: 'top top', end: 'bottom bottom', onUpdate: syncProgress, onRefresh: syncProgress });

    /* Touch devices: one forced stop per chapter (native scroll snapping, active only
       inside the story so the rest of the page scrolls freely). */
    var touch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    if (touch && !reduce && N > 1) {
      var snaps = [];
      for (var si = 0; si < N; si++) {
        var m = document.createElement('div');
        m.className = 'sk-snap';
        m.setAttribute('aria-hidden', 'true');
        section.appendChild(m);
        snaps.push(m);
      }
      var exitSnap = document.createElement('div');
      exitSnap.className = 'sk-snap sk-snap--exit';
      exitSnap.setAttribute('aria-hidden', 'true');
      section.appendChild(exitSnap);

      var placeSnaps = function () {
        var travel = section.offsetHeight - window.innerHeight;
        snaps.forEach(function (m, i) { m.style.top = Math.round((travel * i) / (N - 1)) + 'px'; });
        exitSnap.style.top = section.offsetHeight + 'px';
      };
      placeSnaps();
      ScrollTrigger.addEventListener('refresh', placeSnaps);

      var html = document.documentElement;
      var snapOn = false;
      var updateSnap = function () {
        var r = section.getBoundingClientRect();
        // Active from the first chapter until the page has fully left the story.
        var inside = r.top <= 2 && r.bottom > window.innerHeight * 0.5 + 2;
        if (inside !== snapOn) { snapOn = inside; html.classList.toggle('sk-snap-on', inside); }
      };
      window.addEventListener('scroll', updateSnap, { passive: true });
      updateSnap();
    }

    var lastC = -1;
    function placeText(c) {
      if (Math.abs(c - lastC) < 0.0005) return;
      lastC = c;
      chapters.forEach(function (el, i) {
        var d = c - i;
        var o = clamp01(1 - Math.abs(d) * 2.4);
        if (o === 0 && el._o === 0) return;
        el._o = o;
        el.style.opacity = o.toFixed(3);
        el.style.transform = 'translate3d(0,' + (-d * (isSmall ? 18 : 26)).toFixed(2) + 'vh,0)';
        el.style.visibility = o > 0.01 ? 'visible' : 'hidden';
        el.classList.toggle('is-active', o > 0.6);
      });
    }

    function bgAt(c) {
      var i = Math.max(0, Math.min(N - 1, Math.floor(c)));
      var j = Math.min(N - 1, i + 1);
      var t = smooth(clamp01(c - i));
      return mixHex(poses[i].bg, poses[j].bg, t);
    }


    var bgLayer = section.querySelector('[data-story-bg]');
    var lastBg = '', lastA = -1, lastFrameC = -1;
    function frame() {
      var c = progress * (N - 1);
      if (c === lastFrameC) return;
      lastFrameC = c;
      placeText(c);
      var bg = bgAt(c);
      if (bg !== lastBg) { stage.style.setProperty('--story-bg', bg); lastBg = bg; }
      var aIntro = poses[0].intro ? clamp01(c / 0.75) : 1;
      if (Math.abs(aIntro - lastA) > 0.005) { bgLayer.style.opacity = aIntro.toFixed(3); lastA = aIntro; }
    }
    gsap.ticker.add(frame);

    function loadScript(src) {
      return new Promise(function (res, rej) {
        if (!src) return res();
        var s = document.createElement('script');
        s.src = src; s.onload = function () { res(); }; s.onerror = rej;
        document.head.appendChild(s);
      });
    }
    // Heavy steps never run mid-swipe: on phones the page keeps scrolling on the compositor while
    // the 3D text / canvas froze. They wait for a pause in scrolling (at most ~3 s).
    var lastScroll = 0;
    window.addEventListener('scroll', function () { lastScroll = performance.now(); }, { passive: true });
    function idle(fn) { (window.requestIdleCallback || function (f) { return setTimeout(f, 50); })(fn, { timeout: 120 }); }
    function whenCalm(fn, tries) {
      idle(function () {
        tries = tries || 0;
        if (performance.now() - lastScroll < 400 && tries < 10) setTimeout(function () { whenCalm(fn, tries + 1); }, 300);
        else fn();
      });
    }
    // Resolves on the next calm idle slot: the browser paints between the boot steps instead of
    // freezing for one long block.
    function idleStep(v) { return new Promise(function (res) { whenCalm(function () { res(v); }); }); }

    // Tells the loader (sk-motion.js) it can lift: 3D ready, or given up for the photo fallback.
    function ready() {
      if (window.skStoryReady) return;
      window.skStoryReady = true;
      document.dispatchEvent(new Event('sk:story-ready'));
    }

    // Downloads start now, at low priority, with nothing executed yet: after a reload the boot waits
    // for the hero intro, and downloading only then left the stage empty for ~2 s.
    var glbUrl = section.getAttribute('data-model-src');
    var glbBuffer = glbUrl ? fetch(glbUrl, { priority: 'low' }).then(function (r) { if (!r.ok) throw new Error(r.status); return r.arrayBuffer(); }) : null;
    if (glbBuffer) glbBuffer.catch(function () {}); // failure is handled at build (procedural fallback)
    function preload(href, as, cors) {
      if (!href) return;
      var l = document.createElement('link');
      l.rel = 'preload'; l.as = as; l.href = href; l.fetchPriority = 'low';
      if (cors) l.crossOrigin = 'anonymous'; // must match loadImage(), or the print downloads twice
      document.head.appendChild(l);
    }
    ['data-three-src', 'data-gltf-src', 'data-decal-src', 'data-meshopt-src'].forEach(function (a) { preload(section.getAttribute(a), 'script'); });
    // Colours of the first two chapters: their prints are ready before the story shows
    // (preparing "blanc" on the first swipe was the freeze on "Collection 01").
    function firstColors() {
      return [poses[0] && poses[0].color, poses[1] && poses[1].color].filter(function (k, i, a) { return k && COLORS[k] && a.indexOf(k) === i; });
    }
    var printUrls = JSON.parse(section.getAttribute('data-prints') || '{}');
    firstColors().forEach(function (k) { preload(printUrls[k], 'image', true); });
    function boot() {
      var load = (window.THREE ? Promise.resolve() : loadScript(section.getAttribute('data-three-src')))
        .then(idleStep)
        .then(function () {
          return Promise.all([
            window.THREE.GLTFLoader ? null : loadScript(section.getAttribute('data-gltf-src')),
            window.THREE.DecalGeometry ? null : loadScript(section.getAttribute('data-decal-src')),
            window.MeshoptDecoder ? null : loadScript(section.getAttribute('data-meshopt-src')),
          ]);
        })
        .then(idleStep)
        .then(function () { return window.THREE; });
      var fontsReady = document.fonts && document.fonts.load ? document.fonts.load('500 21px "Noto Serif JP"', '花は桜木人武士').catch(function () {}) : Promise.resolve();
      Promise.all([load, fontsReady]).then(function (r) { build(r[0]); }).catch(noWebGL);
    }

    // No WebGL (hardware acceleration off, blocked script...): show the hoodie photo instead.
    function noWebGL() {
      ready();
      section.classList.add('is-no3d');
      if (stage.querySelector('.sk-story__fallback')) return;
      var urls = JSON.parse(section.getAttribute('data-prints') || '{}');
      var src = urls.noir || urls[Object.keys(urls)[0]];
      if (!src) return;
      var img = document.createElement('img');
      img.className = 'sk-story__fallback';
      img.src = src;
      img.alt = '';
      img.decoding = 'async';
      // Inside the background layer: fades in with it, so it never covers the hero.
      (stage.querySelector('[data-story-bg]') || stage).appendChild(img);
    }

    // Parsing three.js, decoding the model and compiling shaders blocks the main thread for a
    // while. First visit: right away, behind the loader (which waits for ready()). Reloads: once
    // the hero image reveal has finished and the visitor isn't scrolling. The canvas fades in when ready
    // (see .is-3d in sk-base.css).
    var booted = false;
    function bootNow() {
      if (booted) return;
      booted = true;
      boot();
    }
    function bootWhenIdle() { whenCalm(bootNow); }
    if (window.skLoaderHold) bootNow();
    else if (window.skIntroDone || window.skHeroRevealed) bootWhenIdle();
    else {
      document.addEventListener('sk:hero-revealed', bootWhenIdle, { once: true });
      document.addEventListener('sk:intro-done', bootWhenIdle, { once: true });
    }
    setTimeout(bootNow, 6000); // safety net if the intro never reports

    // r147 keeps ColorManagement in legacy mode: hex colours must be converted to linear by hand.
    function setLin(THREE, color, hex) {
      color.set(hex);
      if (!THREE.ColorManagement || THREE.ColorManagement.legacyMode !== false) color.convertSRGBToLinear();
      return color;
    }

    function srgb(THREE, tex) {
      if ('colorSpace' in tex && THREE.SRGBColorSpace) tex.colorSpace = THREE.SRGBColorSpace;
      else if (THREE.sRGBEncoding) tex.encoding = THREE.sRGBEncoding;
      return tex;
    }

    function loadImage(url) {
      return new Promise(function (res, rej) {
        var img = new Image();
        img.crossOrigin = 'anonymous';
        // decode() decodes off the main thread, so drawImage() in printCanvas doesn't stall a frame.
        img.onload = function () { (img.decode ? img.decode() : Promise.resolve()).then(function () { res(img); }, function () { res(img); }); };
        img.onerror = rej;
        img.src = url;
      });
    }

    // Crop the print area of a product photo and feather its edges.
    function printCanvas(img) {
      var cw = 640, ch = 1160;
      var c = document.createElement('canvas');
      c.width = cw; c.height = ch;
      var g = c.getContext('2d');
      var sx = img.width * 0.27, sy = img.height * 0.19, sw = img.width * 0.46, sh = img.height * 0.68;
      g.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
      g.globalCompositeOperation = 'destination-in';
      // Elliptical feather matching the crop.
      g.save();
      g.setTransform(1, 0, 0, ch / cw, 0, 0);
      var m = g.createRadialGradient(cw / 2, cw / 2, cw * 0.26, cw / 2, cw / 2, cw * 0.5);
      m.addColorStop(0, 'rgba(0,0,0,1)');
      m.addColorStop(0.7, 'rgba(0,0,0,0.85)');
      m.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = m;
      g.fillRect(0, 0, cw, cw);
      g.restore();
      return c;
    }

    function build(THREE) {
      var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
      // Sharp by default (native density up to 2x); lowered automatically only if the device can't keep up.
      var maxDpr = Math.min(window.devicePixelRatio || 1, 2);
      // Weak devices (little memory / few cores) start at a lower density and may drop further,
      // instead of stuttering through the ~1.5 s the adaptive loop needs to react.
      // (deviceMemory is Chrome-only; iOS Safari caps hardwareConcurrency, so only <= 2 counts there.)
      var lowEnd = (navigator.deviceMemory && navigator.deviceMemory <= 3) || (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2);
      var minDpr = lowEnd ? 0.85 : 1.1;
      var dpr = Math.min(isSmall ? Math.min(maxDpr, 1.5) : maxDpr, lowEnd ? 1.1 : 2);
      renderer.setPixelRatio(dpr);
      if ('outputColorSpace' in renderer && THREE.SRGBColorSpace) renderer.outputColorSpace = THREE.SRGBColorSpace;
      else renderer.outputEncoding = THREE.sRGBEncoding;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.05;
      // Asked once, now, while the GPU is idle: the first query waits on the GPU queue. 4x is plenty
      // for a print seen mostly head-on, and cheaper to sample than 16x on phones.
      var aniso = Math.min(4, renderer.capabilities.getMaxAnisotropy());

      var scene = new THREE.Scene();
      var camera = new THREE.PerspectiveCamera(30, 1, 0.1, 100);

      var hemi = new THREE.HemisphereLight(0xfff6ee, 0x221a1c, 1.1); scene.add(hemi);
      var key = new THREE.DirectionalLight(0xffffff, 2.2); key.position.set(3, 4, 6); scene.add(key);
      var rimL = new THREE.DirectionalLight(0xffc3cf, 2.4); rimL.position.set(-5, 2, -4); scene.add(rimL);
      var fill = new THREE.DirectionalLight(0xbfd2ff, 0.6); fill.position.set(-4, -1, 5); scene.add(fill);
      var backL = new THREE.DirectionalLight(0xffffff, 1.4); backL.position.set(2, 3, -6); scene.add(backL);
      if (isSmall) { fill.visible = false; }

      var pivot = new THREE.Group();
      scene.add(pivot);

      // The active hoodie implementation: { setColor(key), bounds:{minX,maxX,minY,maxY,minZ,maxZ} }
      var model = null;
      var targetExposure = 1.05;
      var currentColor = null;
      var pendingColor = poses[0].color || 'blanc';

      function setColor(key) {
        if (!COLORS[key]) return;
        pendingColor = key;
        if (!model || key === currentColor) return;
        currentColor = key;
        model.setColor(key);
        var lumi = (function (h) { var c = hexToRgb(h); return (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]) / 255; })(COLORS[key].base);
        var glb = model.bounds && !model.bounds.procedural;
        targetExposure = lumi < 0.35 ? (glb ? 0.9 : 0.85) : (glb ? 1.0 : 1.05);
        if (nameEl) nameEl.textContent = key.replace(/^./, function (m) { return m.toUpperCase(); });
      }


      /* ----- Real model (GLB) with the product print projected on the back ----- */
      function useGLB(gltf) {
        var root = gltf.scene;
        var meshes = [], baked = null;
        root.traverse(function (o) { if (!o.isMesh) return; if (o.name === 'sk_print') baked = o; else meshes.push(o); });
        if (!meshes.length) throw new Error('empty model');
        var hide = (section.getAttribute('data-hide-meshes') || '').toLowerCase().split(',').map(function (s) { return s.trim(); }).filter(Boolean);
        meshes.forEach(function (m) { if (hide.some(function (h) { return (m.name || '').toLowerCase().indexOf(h) !== -1 || ((m.material && m.material.name) || '').toLowerCase().indexOf(h) !== -1; })) m.visible = false; });

        // Normalise: centre and scale to ~2.8 units tall.
        root.updateMatrixWorld(true);
        var box = new THREE.Box3();
        meshes.forEach(function (m) { if (m.visible) box.expandByObject(m); });
        var size = box.getSize(new THREE.Vector3()), center = box.getCenter(new THREE.Vector3());
        var s = 2.8 / size.y;
        var holder = new THREE.Group();
        root.position.sub(center);
        holder.add(root);
        holder.scale.setScalar(s);
        pivot.add(holder);
        holder.updateMatrixWorld(true);

        // A real cloth mesh needs softer light than the inflated fallback, or dark colours turn grey.
        hemi.intensity = 0.55; key.intensity = 1.5; rimL.intensity = 1.5; fill.intensity = 0.35; backL.intensity = 0.8;
        var fabricMats = [];
        meshes.forEach(function (m) {
          if (!m.visible) return;
          var old = m.material;
          var mat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.88, metalness: 0 });
          if (old && old.normalMap) { mat.normalMap = old.normalMap; mat.normalScale = old.normalScale ? old.normalScale.clone() : new THREE.Vector2(1, 1); }
          if (old && old.aoMap) mat.aoMap = old.aoMap;
          mat.side = THREE.DoubleSide;
          m.material = mat;
          fabricMats.push(mat);
        });

        // Main body = mesh with the most vertices.
        var body = meshes.filter(function (m) { return m.visible; }).sort(function (a, b) { return b.geometry.attributes.position.count - a.geometry.attributes.position.count; })[0];
        var wb = new THREE.Box3().setFromObject(holder);
        var W = wb.max.x - wb.min.x, H = wb.max.y - wb.min.y, D = wb.max.z - wb.min.z;

        var targetCol = new THREE.Color(1, 1, 1), pendingPrint = null, colorInit = false;
        // Transparent 1px stand-in: the decal shader is compiled with a map from the start, so the
        // real print arriving later is a plain texture swap, not a second shader compile mid-scroll.
        var blank = srgb(THREE, new THREE.DataTexture(new Uint8Array([255, 255, 255, 0]), 1, 1));
        blank.needsUpdate = true;
        var decalMat = new THREE.MeshStandardMaterial({ map: blank, opacity: 0, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -4, roughness: 0.85 });
        // Second layer on the same geometry: the next print fades in OVER the current one, so the
        // back is never bare during a colour change (it used to fade out to nothing first).
        var nextMat = decalMat.clone();
        var decal = null, printTop = null;
        var printJob = {};
        var urls = JSON.parse(section.getAttribute('data-prints') || '{}');

        function makeDecal() {
          if (decal) return;
          var inv = new THREE.Matrix4().copy(pivot.matrixWorld).invert();
          pivot.updateMatrixWorld(true);
          var geo;
          if (baked) {
            // Same projection, precomputed in the GLB (models/bake-print.mjs): DecalGeometry blocked
            // phones for 0.1–0.45 s. Its normals arrive as interleaved int8 (meshopt): back to plain
            // floats before transforming them, or the print shading streaks.
            geo = baked.geometry.clone();
            var n = geo.attributes.normal;
            if (n && (n.normalized || n.isInterleavedBufferAttribute)) {
              var il = n.isInterleavedBufferAttribute, src = il ? n.data.array : n.array, stride = il ? n.data.stride : n.itemSize, off = n.offset || 0;
              var k = n.normalized ? 1 / (src.BYTES_PER_ELEMENT === 1 ? 127 : 32767) : 1, f = new Float32Array(n.count * 3);
              for (var i = 0; i < n.count; i++) for (var c = 0; c < 3; c++) f[i * 3 + c] = Math.max(src[i * stride + off + c] * k, -1);
              geo.setAttribute('normal', new THREE.BufferAttribute(f, 3));
            }
            geo.applyMatrix4(baked.matrixWorld);
            baked.parent.remove(baked);
          } else {
            if (!THREE.DecalGeometry) return;
            var pw = W * 0.46, ph = pw / (640 / 1160);
            var pos = new THREE.Vector3((wb.min.x + wb.max.x) / 2, wb.min.y + H * 0.48, wb.min.z + D * 0.02);
            geo = new THREE.DecalGeometry(body, pos, new THREE.Euler(0, Math.PI, 0), new THREE.Vector3(pw, ph, D * 0.9));
          }
          geo.applyMatrix4(inv);
          decal = new THREE.Mesh(geo, decalMat);
          pivot.add(decal);
          printTop = new THREE.Mesh(geo, nextMat);
          printTop.renderOrder = 1;
          printTop.visible = false; // drawn only during a cross-fade: an invisible layer still costs a full draw
          pivot.add(printTop);
        }

        // One job per print, shared: the first two start right away and run alongside the shader
        // compile instead of one after the other at the end.
        function prepPrint(key) {
          if (!urls[key]) return Promise.resolve();
          return printJob[key] || (printJob[key] = loadImage(urls[key]).then(function (img) {
            var t = srgb(THREE, new THREE.CanvasTexture(printCanvas(img)));
            t.anisotropy = aniso;
            if (renderer.initTexture) renderer.initTexture(t);
            return t;
          }).catch(function () {}));
        }
        firstColors().forEach(prepPrint);

        // Pose the hoodie at rest (back towards -z) before projecting the decal.
        pivot.rotation.set(0, 0, 0); pivot.position.set(0, 0, 0); pivot.updateMatrixWorld(true);
        makeDecal();

        model = {
          prep: prepPrint,
          bounds: { minX: wb.min.x, maxX: wb.max.x, minY: wb.min.y, maxY: wb.max.y, minZ: wb.min.z, maxZ: wb.max.z },
          // Colour changes are eased (fabric tint + print cross-fade) so nothing ever pops.
          setColor: function (key) {
            setLin(THREE, targetCol, COLORS[key].base);
            if (!colorInit) { fabricMats.forEach(function (m) { m.color.copy(targetCol); }); colorInit = true; }
            prepPrint(key).then(function (t) {
              if (currentColor !== key || !t) return;
              if (decalMat.map === blank) { decalMat.map = t; decalMat.opacity = 0; pendingPrint = null; }
              else if (decalMat.map !== t) pendingPrint = t;
              else { pendingPrint = null; nextMat.opacity = 0; if (printTop) printTop.visible = false; } // back to the print already shown
            });
          },
          update: function (dt) {
            var a = 1 - Math.exp(-dt * 9);
            fabricMats.forEach(function (m) { m.color.lerp(targetCol, a); });
            if (pendingPrint) {
              if (nextMat.map !== pendingPrint) { nextMat.map = pendingPrint; nextMat.opacity = 0; }
              if (printTop) printTop.visible = true;
              nextMat.opacity = Math.min(1, nextMat.opacity + dt * 5);
              if (nextMat.opacity === 1) { decalMat.map = pendingPrint; decalMat.opacity = 1; nextMat.opacity = 0; pendingPrint = null; if (printTop) printTop.visible = false; }
            } else if (decalMat.map !== blank && decalMat.opacity < 1) {
              decalMat.opacity = Math.min(1, decalMat.opacity + dt * 5);
            }
          },
        };
        // Preload the other prints when the browser is idle AND the visitor isn't scrolling:
        // decoding + uploading one print costs a frame or two on phones.
        ORDER.forEach(function (k, i) { setTimeout(function () { whenCalm(function () { prepPrint(k); }); }, 2500 + i * 700); });
      }

      /* ----- Fallback: procedural inflated hoodie ----- */
      function useProcedural() {
        var TEX = isSmall ? 1024 : 2048;
        function makeCanvas() { var c = document.createElement('canvas'); c.width = Math.round(TEX * 600 / 700); c.height = TEX; return c; }
        var knit = knitNormal(THREE, 128);
        knit.repeat.set(26, 30);
        var tex = {};
        function texFor(key) {
          if (tex[key]) return tex[key];
          var f = makeCanvas(), b = makeCanvas();
          paintFront(f, key); paintBack(b, key);
          var tf = srgb(THREE, new THREE.CanvasTexture(f)), tb = srgb(THREE, new THREE.CanvasTexture(b));
          [tf, tb].forEach(function (t) { t.anisotropy = aniso; if (renderer.initTexture) renderer.initTexture(t); });
          tex[key] = { f: tf, b: tb };
          return tex[key];
        }
        var first = texFor(pendingColor);
        var h = buildHoodie(THREE, first.f, first.b, knit);
        pivot.add(h.group);
        model = {
          bounds: { minX: -1.5, maxX: 1.5, minY: -1.75, maxY: 1.75, minZ: h.backZ / 200, maxZ: h.frontZ / 200, procedural: true },
          setColor: function (key) {
            var t = texFor(key);
            h.mats[0].map = t.f; h.mats[1].map = t.b;
            h.mats.forEach(function (m) { m.needsUpdate = true; });
            setLin(THREE, h.sideMat.color, COLORS[key].base);
            var lu = (function (hx) { var c = hexToRgb(hx); return (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]) / 255; })(COLORS[key].base);
            h.mats.forEach(function (m) { if (m.sheenColor) { setLin(THREE, m.sheenColor, shade(COLORS[key].base, lu < 0.35 ? 0.2 : 0.5)); m.sheen = lu < 0.35 ? 0.45 : 1; } });
          },
        };
        // Pre-render every colour while idle so scrolling never waits on painting.
        ORDER.forEach(function (k, i) { setTimeout(function () { idle(function () { texFor(k); }); }, 300 + i * 200); });
      }

      var modelReady = (glbBuffer && THREE.GLTFLoader)
        ? glbBuffer.then(function (buf) {
            return new Promise(function (res, rej) {
              var loader = new THREE.GLTFLoader();
              // sk-hoodie.glb is EXT_meshopt_compression-encoded (~60% smaller on mobile).
              if (window.MeshoptDecoder) loader.setMeshoptDecoder(window.MeshoptDecoder);
              loader.parse(buf, '', res, rej);
            });
          }).then(useGLB).catch(function () { useProcedural(); })
        : Promise.resolve(useProcedural());

      // Contact shadow
      var sc = document.createElement('canvas'); sc.width = sc.height = 128;
      var sg = sc.getContext('2d'); var rg = sg.createRadialGradient(64, 64, 4, 64, 64, 64);
      rg.addColorStop(0, 'rgba(0,0,0,0.45)'); rg.addColorStop(1, 'rgba(0,0,0,0)'); sg.fillStyle = rg; sg.fillRect(0, 0, 128, 128);
      var shadow = new THREE.Mesh(new THREE.PlaneGeometry(3.2, 0.8), new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(sc), transparent: true, depthWrite: false }));
      shadow.rotation.x = -Math.PI / 2;
      scene.add(shadow);

      // Petals
      var shape = new THREE.Shape();
      shape.moveTo(0, 0); shape.bezierCurveTo(0.05, 0.03, 0.06, 0.1, 0, 0.15); shape.bezierCurveTo(-0.06, 0.1, -0.05, 0.03, 0, 0);
      var petalGeo = new THREE.ShapeGeometry(shape, 4); petalGeo.translate(0, -0.075, 0);
      var COUNT = isSmall ? 16 : 70;
      var petals = new THREE.InstancedMesh(petalGeo, new THREE.MeshBasicMaterial({ color: 0xf4b8c6, side: THREE.DoubleSide, transparent: true, opacity: 0.9 }), COUNT);
      var R = rng(77), seeds = [], dummy = new THREE.Object3D();
      for (var k = 0; k < COUNT; k++) seeds.push({ x: (R() - 0.5) * 9, y: (R() - 0.5) * 7, z: (R() - 0.5) * 6 - 1, v: 0.2 + R() * 0.35, ph: R() * TAU, sp: 0.4 + R() * 1.4, s: 0.6 + R() * 0.9 });
      scene.add(petals);

      // Every setSize reallocates the GPU buffers: skip it when nothing changed (the ResizeObserver
      // fires once right after boot with the same size).
      var lastW = 0, lastH = 0;
      function resize() {
        var w = stage.clientWidth, h = stage.clientHeight;
        if (w === lastW && h === lastH) return;
        lastW = w; lastH = h;
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
      }
      resize();
      if (window.ResizeObserver) new ResizeObserver(resize).observe(stage);

      var pointer = { x: 0, y: 0 }, pt = { x: 0, y: 0 };
      if (!isSmall) window.addEventListener('pointermove', function (e) { pointer.x = e.clientX / window.innerWidth - 0.5; pointer.y = e.clientY / window.innerHeight - 0.5; }, { passive: true });

      var state = { ry: poses[0].ry, rx: poses[0].rx, dist: poses[0].dist, ox: 0, oy: 0, sy: poses[0].sy };
      var v3 = new THREE.Vector3(), eul = new THREE.Euler();
      var visible = true;
      new IntersectionObserver(function (e) { visible = e[0].isIntersecting; }).observe(section);
      var clock = new THREE.Clock();

      function focusOffset(p, ry, rx, out) {
        var b = model.bounds;
        var fx = p.fx / 600, fy = p.fy / 700;
        var x = p.face === 'back' ? lerp(b.maxX, b.minX, fx) : lerp(b.minX, b.maxX, fx);
        var y = lerp(b.maxY, b.minY, fy);
        var z = p.face === 'back' ? b.minZ : b.maxZ;
        out.set(x, y, z);
        eul.set(rx, ry, 0, 'YXZ');
        out.applyEuler(eul);
        return out;
      }
      var oa = new THREE.Vector3(), ob = new THREE.Vector3();
      var spinAcc = 0, parkTime = 0;

      // Compile every shader in its own idle slot before the first visible frame, so the
      // first render isn't one long freeze.
      modelReady.then(idleStep).then(function () {
        renderer.compile(scene, camera);
        return idleStep();
      }).then(function () {
        // Same for the textures (the model's 1024px normal map...): uploaded now, not in frame one.
        if (renderer.initTexture) scene.traverse(function (o) {
          var m = o.material;
          if (m) [m.map, m.normalMap, m.aoMap].forEach(function (t) { if (t) renderer.initTexture(t); });
        });
        return idleStep();
      }).then(function () {
        // The prints of the first two chapters (noir -> blanc) are ready before the story shows:
        // preparing "blanc" on the first swipe was the freeze on "Collection 01".
        if (!model.prep) return null;
        return Promise.all(firstColors().map(model.prep)).then(idleStep);
      }).then(function () {
        setColor(pendingColor);
        section.classList.add('is-3d');
        gsap.ticker.add(tick);
        ready();
      }).catch(noWebGL);

      // Adaptive resolution: if frames get slow, render fewer pixels; recover when smooth again.
      // The first ~1.5 s are skipped: textures still uploading make them slow on every device.
      var slow = 0, fast = 0, warm = 0;
      function adapt(dt) {
        if (++warm < 90) return;
        if (dt > 0.024) { slow++; fast = 0; } else if (dt < 0.0175) { fast++; slow = 0; }
        // setPixelRatio already resizes the drawing buffer; no second setSize.
        if (slow > 20 && dpr > minDpr) { dpr = Math.max(minDpr, dpr - 0.3); renderer.setPixelRatio(dpr); slow = 0; }
        else if (fast > 240 && dpr < maxDpr) { dpr = Math.min(maxDpr, dpr + 0.15); renderer.setPixelRatio(dpr); fast = 0; }
      }

      function tick() {
        if (!visible) return;
        var dt = Math.min(clock.getDelta(), 0.05), t = clock.elapsedTime;
        var c = progress * (N - 1);
        var i = Math.max(0, Math.min(N - 1, Math.floor(c))), j = Math.min(N - 1, i + 1), f = smooth(clamp01(c - i));
        var A = poses[i], B = poses[j];
        var ry = lerp(A.ry, B.ry, f), rx = lerp(A.rx, B.rx, f), dist = lerp(A.dist, B.dist, f), sy = lerp(A.sy, B.sy, f);
        focusOffset(A, A.ry, A.rx, oa); focusOffset(B, B.ry, B.rx, ob);
        var ox = -lerp(oa.x, ob.x, f), oy = -lerp(oa.y, ob.y, f);

        // Parked on the colour chapter: slow turntable + colours cycling on their own.
        var parkedIdx = Math.round(c);
        var parked = poses[parkedIdx].cycle && Math.abs(c - parkedIdx) < 0.2;
        if (parked) { spinAcc += dt * 0.55; parkTime += dt; }
        else { spinAcc += (Math.round(spinAcc / TAU) * TAU - spinAcc) * 0.06; parkTime = 0; }
        ry += spinAcc;

        var k = isSmall ? 0.12 : 0.1;
        // Cap the turning speed so big rotations never whip around after a quick swipe.
        var maxTurn = (isSmall ? 2.2 : 3.5) * dt;
        var dRy = (ry - state.ry) * k;
        state.ry += dRy > maxTurn ? maxTurn : dRy < -maxTurn ? -maxTurn : dRy; state.rx += (rx - state.rx) * k; state.sy += (sy - state.sy) * k;
        state.dist += (dist - state.dist) * k; state.ox += (ox - state.ox) * k; state.oy += (oy - state.oy) * k;

        pt.x += (pointer.x - pt.x) * 0.05; pt.y += (pointer.y - pt.y) * 0.05;
        pivot.rotation.set(state.rx + pt.y * 0.15, state.ry + pt.x * 0.25 + Math.sin(t * 0.6) * 0.03, Math.sin(t * 0.5) * 0.02, 'YXZ');

        var portrait = camera.aspect < 0.8;
        var camDist = portrait ? state.dist * (1.25 + 0.3 * clamp01((state.dist - 3.3) / 4)) : state.dist;
        var visH = 2 * camDist * Math.tan((camera.fov * Math.PI) / 360);
        var screenX = portrait ? 0 : camDist * 0.13 * (1 - clamp01(-state.sy * 3));
        var screenY = (portrait ? camDist * 0.085 : 0) + state.sy * visH;
        pivot.position.set(state.ox + screenX, state.oy + screenY + Math.sin(t * 0.9) * 0.04, 0);
        shadow.position.set(pivot.position.x, pivot.position.y - 1.75, 0);
        shadow.material.opacity = clamp01((state.dist - 5) / 2) * (1 - clamp01(-state.sy * 4));
        camera.position.set(0, 0, camDist);
        camera.lookAt(0, 0, 0);

        var key = null;
        var nearest = poses[Math.round(c)];
        if (nearest.cycle) {
          var local = clamp01(c - Math.round(c) + 0.5);
          // Only cycle once the visitor has stopped on the chapter; while passing through, keep the colour.
          key = parked ? ORDER[Math.floor(parkTime / 1.1) % ORDER.length] : null;
        } else if (nearest.color) key = nearest.color;
        if (key) setColor(key);
        if (model.update) model.update(dt);
        renderer.toneMappingExposure += (targetExposure - renderer.toneMappingExposure) * (1 - Math.exp(-dt * 6));

        for (var n = 0; n < COUNT; n++) {
          var s = seeds[n];
          s.y -= s.v * dt;
          if (s.y < -3.6) s.y = 3.6;
          dummy.position.set(s.x + Math.sin(t * 0.7 + s.ph) * 0.4, s.y, s.z);
          dummy.rotation.set(t * s.sp + s.ph, t * s.sp * 0.7, s.ph);
          dummy.scale.setScalar(s.s);
          dummy.updateMatrix();
          petals.setMatrixAt(n, dummy.matrix);
        }
        petals.instanceMatrix.needsUpdate = true;
        renderer.render(scene, camera);
        adapt(dt);
      }
    }
  }

  function init(scope) { scope.querySelectorAll('[data-sk-story]').forEach(setup); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target); ScrollTrigger.refresh(); });
})();
